import { useEffect, useRef, useState } from "react";

import {
  makeDeleteApiCall,
  makeGetApiCall,
  makePostApiCall,
  makePostApiCallToPrinter,
  makePostBlobApiCall,
  makePutApiCall,
} from "../../shared/makeApiCall";
import quickBooksInvoicesApi, {
  isActiveInvoiceStatus,
} from "../../services/quickBooksInvoicesApi";
import { useQuickBooksInvoiceStatus } from "../../hooks/useQuickBooksInvoiceStatus";
import { fetchUsersForDropdown } from "../../shared/usersForDropdown";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import { generateUniqueNumber } from "../../shared/utility";
import ProgressModal from "../../components/modals/ProgressModal";

function filenameFromContentDisposition(header) {
  if (!header) return null;
  const match = /filename\*?=(?:UTF-8''|"?)([^";]+)"?/i.exec(header);
  if (!match) return null;
  const raw = match[1].replace(/"/g, "").trim();
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export function ShippingDetailsViewModel(shippingId, logout, addSnackbar) {
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [invoiceQueueEnabled, setInvoiceQueueEnabled] = useState(false);
  const onInvoiceCompleted = useRef(() => {});
  const quickBooksInvoice = useQuickBooksInvoiceStatus(shippingId, {
    onCompleted: (request, context) => onInvoiceCompleted.current(request, context),
  });
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [shipping, setShipping] = useState(null);
  const [invalidOrderItemIds, setInvalidOrderItemIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPrintingShipping, setIsPrintingShipping] = useState(false);
  const printingShippingRef = useRef(false);
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });

  const showProgressModal = (message = "Processing...") => {
    setEmptyModelOpen({
      childComponent: <ProgressModal message={message} />,
      open: true,
    });
  };

  const hideProgressModal = () => {
    setEmptyModelOpen({
      childComponent: null,
      open: false,
    });
  };

  useEffect(() => {
    let active = true;
    quickBooksInvoicesApi
      .getCapabilities()
      .then((capabilities) => {
        if (active) setInvoiceQueueEnabled(Boolean(capabilities.enabled));
      })
      .catch(() => {
        if (active) setInvoiceQueueEnabled(false);
      });
    return () => {
      active = false;
    };
  }, [shippingId]);

  onInvoiceCompleted.current = (request, context) => {
    if (request?.quickbooks_invoice_id) {
      setShipping((current) =>
        current
          ? {
              ...current,
              quickbooks_invoice_id: request.quickbooks_invoice_id,
              invoice_number: request.invoice_number ?? current.invoice_number,
            }
          : current,
      );
    }
    if (!context?.live) {
      return;
    }
    addSnackbar(
      new SnackbarData(
        generateUniqueNumber(),
        "QuickBooks invoice created",
        SnackbarType.Success,
      ),
    );
    getShippingDetails();
  };

  const getShippingDetails = async function () {
    let callResult = await makePostApiCall(`fetchShipping/${shippingId}`);
    if (callResult.success) {
      const orderDetails = await onFetchOrderItems(callResult.data.order_id);
      if (orderDetails.success) {
        const order = orderDetails.data;
        const shipmentItemsResult = await makeGetApiCall(
          `getShippingItems/${shippingId}`,
        );
        const shipmentLines = Array.isArray(
          shipmentItemsResult.data?.shipment_items,
        )
          ? shipmentItemsResult.data.shipment_items
          : [];
        const shipmentLinesById = new Map(
          shipmentLines.map((item) => [Number(item.id), item]),
        );
        setShipping({
          ...callResult.data,
          order_id: order.id,
          customer: order.customer,
          orderItems: order.products.map((item) => ({
            ...item,
            ...(shipmentLinesById.get(Number(item.id)) ?? {}),
          })),
        });

        setLoading(false);
      } else {
        setLoading(false);
      }
    } else {
      setLoading(false);
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const onFetchOrderItems = async function (id) {
    let callResult = await makePostApiCall(`fetchOrder/${id}`);
    if (callResult.success) {
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  const updateShippingApiCall = async function (payload) {
    const nextStatus =
      typeof payload?.status === "object"
        ? payload.status?.value
        : payload?.status;
    const currentStatus =
      typeof shipping?.status === "object"
        ? shipping.status?.value
        : (shipping?.status ?? shipping?.legacy_order_status);
    const requestPayload = {
      ...payload,
      status: payload.status,
    };
    delete requestPayload.product_condition;
    let callResult = await makePostApiCall(
      `updateShipping/${shippingId}`,
      requestPayload,
    );
    if (callResult.success) {
      setInvalidOrderItemIds([]);
      // A shipment marked Shipped again must not create a second invoice.
      // Changing the status does not void the invoice that is already there.
      const hasQuickbooksInvoice = [
        payload?.quickbooks_invoice_id,
        shipping?.quickbooks_invoice_id,
      ].some((id) => Number(id) > 0);
      const shouldCreateInvoiceInQb =
        nextStatus === 5 && currentStatus !== 5 && !hasQuickbooksInvoice;

      if (shouldCreateInvoiceInQb && !invoiceRequestInProgress()) {
        await createInvoiceInQb();
      } else {
        addSnackbar(
          new SnackbarData(
            generateUniqueNumber(),
            callResult.data.message,
            SnackbarType.Success,
          ),
        );
      }

      await getShippingDetails();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      const invalidIds = Array.isArray(callResult.data?.invalid_order_item_ids)
        ? callResult.data.invalid_order_item_ids
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id))
        : [];
      setInvalidOrderItemIds(invalidIds);
      showErrorModal(callResult.data?.message);
      await getShippingDetails();
    }
    return callResult;
  };

  const onFetchPackagedItemByBarcode = async function (scannedBarcode) {
    let callResult = await makePostApiCall(
      `fetchPackagedItemByBarcode/${scannedBarcode}`,
    );
    if (callResult.success) {
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const addPackagedItemToOrderItem = async function (
    shippingId,
    orderItem,
    packagedItem,
    productCondition,
  ) {
    const payload = {
      shippingId: shippingId,
      orderItem: orderItem,
      packagedItem: packagedItem,
      ...(productCondition ? { productCondition } : {}),
    };
    let callResult = await makePostApiCall(
      "addPackagedItemToOrderItem",
      payload,
    );
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Package added to shipping",
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const removeShippedCartonApiCall = async function (carton) {
    const callResult = await makePostApiCall(`removeShippedCarton`, {
      id: carton.id,
      barcode: carton.barcode,
      carton_type: carton.carton_type,
    });
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const getUsersForDropdown = async function (type, department) {
    let callResult = await fetchUsersForDropdown(type, department);

    if (callResult.success) {
      setEmployeeOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const deleteShipping = async function () {
    let callResult = await makePostApiCall(`shippings/delete/${shippingId}`);

    if (callResult.success) {
      window.location = "/shippings";
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.error);
    }
  };

  const printShipping = async function () {
    if (printingShippingRef.current) return;

    printingShippingRef.current = true;
    setIsPrintingShipping(true);
    try {
      const callResult = await makePostBlobApiCall(
        `printShipping/${shippingId}`,
      );

      if (callResult.success) {
        downloadBlob(
          callResult.data,
          filenameFromContentDisposition(
            callResult.headers?.["content-disposition"],
          ) ?? "shipment.pdf",
        );
      } else {
        if (callResult.errorStatus === 401) {
          logout();
        }
        showErrorModal(
          callResult.data?.message ?? "We could not print this shipment.",
        );
      }
    } finally {
      printingShippingRef.current = false;
      setIsPrintingShipping(false);
    }
  };

  const quickAddPackagedItemToOrderItem = async function (
    shippingId,
    orderItemId,
    lotItem,
    original_scanned_amount,
  ) {
    const payload = {
      shippingId: shippingId,
      orderItemId: orderItemId,
      lotItem: lotItem,
      original_scanned_amount: original_scanned_amount,
    };
    let callResult = await makePostApiCall(
      "quickAddPackagedItemToOrderItem",
      payload,
    );
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Package(s) added to shipping",
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const fetchPackagedItemsForQuickScan = async function (payload) {
    let callResult = await makePostApiCall(
      `fetchPackagedItemsForQuickScan`,
      payload,
    );
    if (callResult.success) {
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const invoiceRequestInProgress = () => {
    const current = quickBooksInvoice.status;
    return (
      quickBooksInvoice.polling ||
      quickBooksInvoice.retrying ||
      (current &&
        (isActiveInvoiceStatus(current.status) ||
          ["failed", "needs_review", "blocked", "succeeded"].includes(current.status)))
    );
  };

  const acceptQueuedInvoice = (callResult) => {
    if (callResult?.data?.status === "succeeded" && callResult.data.quickbooks_invoice_id) {
      setShipping((current) =>
        current
          ? {
              ...current,
              quickbooks_invoice_id: callResult.data.quickbooks_invoice_id,
              invoice_number: callResult.data.invoice_number ?? current.invoice_number,
            }
          : current,
      );
      return true;
    }
    if (callResult?.data?.request_id && callResult.data.status) {
      quickBooksInvoice.refresh();
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message || "Invoice queued",
          SnackbarType.Success,
        ),
      );
      return true;
    }
    return false;
  };

  const createInvoiceInQb = async function () {
    let queueEnabled = invoiceQueueEnabled;
    try {
      const capabilities = await quickBooksInvoicesApi.getCapabilities();
      queueEnabled = Boolean(capabilities.enabled);
      setInvoiceQueueEnabled(queueEnabled);
    } catch {
      queueEnabled = invoiceQueueEnabled;
    }

    if (!queueEnabled) {
      showProgressModal("Sending shipping to Quickbooks...");
    }
    let callResult = await makePostApiCall(`quickbooks/createInvoice`, {
      shipment_id: shippingId,
    });
    if (!queueEnabled) {
      hideProgressModal();
    }
    if (callResult.success && acceptQueuedInvoice(callResult)) {
      return callResult;
    }
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Shipping sent to Quickbooks",
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      if (callResult.data?.code === "invoice_queue_enabled") {
        quickBooksInvoice.refresh();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const normalizeFlatShipmentItems = function (items = []) {
    return items.map((item) => ({
      id: item.id,
      shipment_item_id: item.id,
      barcode: item.barcode,
      lot_number: item.lot_number,
      packing_size: item.packing_size,
      packaging_date: item.picked_at,
      case_quantity: item.case_quantity ?? 0,
      carton_type: item.carton_type,
      product_id: item.product_id,
      product_name: item.product_name,
      plu: item.plu,
      serial_number: item.serial_number,
      status: item.status,
      return_status: item.return_status,
      credit_status: item.credit_status,
      description_en: item.description,
      product_temperature: item.product_temperature,
      order_item_id: item.order_item_id,
      is_quick_scan: item.is_quick_scan ?? false,
    }));
  };

  const getOrderItemShipments = async function (id) {
    const callResult = await makeGetApiCall(`getShippingItems/${shippingId}`);
    if (callResult.success) {
      const flatItems = Array.isArray(callResult.data?.flat_shipment_items)
        ? callResult.data.flat_shipment_items
        : [];
      const filteredItems = flatItems.filter(
        (item) => parseInt(item.order_item_id, 10) === parseInt(id, 10),
      );
      return { success: true, data: normalizeFlatShipmentItems(filteredItems) };
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
      return { success: false };
    }
  };

  const handlePriceSplitResult = async (callResult, message) => {
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(generateUniqueNumber(), message, SnackbarType.Success),
      );
      await getShippingDetails();
    } else {
      if (callResult.errorStatus === 401) logout();
      showErrorModal(
        callResult.data?.message ?? "Unable to update shipment pricing.",
      );
    }
    return callResult;
  };

  const onCreatePriceSplit = async (orderItemId, payload) =>
    handlePriceSplitResult(
      await makePostApiCall(
        `shippings/${shippingId}/orderItems/${orderItemId}/priceSplits`,
        payload,
      ),
      "Shipment price added",
    );

  const onUpdatePriceSplit = async (orderItemId, splitId, payload) =>
    handlePriceSplitResult(
      await makePutApiCall(
        `shippings/${shippingId}/orderItems/${orderItemId}/priceSplits/${splitId}`,
        payload,
      ),
      "Shipment price updated",
    );

  const onDeletePriceSplit = async (orderItemId, splitId) =>
    handlePriceSplitResult(
      await makeDeleteApiCall(
        `shippings/${shippingId}/orderItems/${orderItemId}/priceSplits/${splitId}`,
      ),
      "Shipment price deleted",
    );

  const setProductTemperature = async function (
    productId,
    temperature,
    productCondition,
  ) {
    let callResult = await makePostApiCall(`setProductTemperature`, {
      shipping_id: shippingId,
      product_id: productId,
      temperature: temperature,
      productCondition,
    });
    if (callResult.success) {
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const createShippingRecordByID = async function () {
    let callResult = await makePostApiCall(
      `/qaReports/createShippingRecordByID`,
      {
        shippingId: shippingId,
      },
    );
    if (callResult.success) {
      window.open(callResult.data.location, "_blank");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
      return null;
    }
  };

  const printShippingLabelToPrinter = async function (labelPayload) {
    const printResult = await makePostApiCallToPrinter({
      ...labelPayload,
    });
    if (!printResult?.success) {
      const errorMessage =
        printResult?.data?.subtitle ??
        printResult?.data?.message ??
        printResult?.data?.title ??
        "Print failed. Please try again.";
      showErrorModal("Print error", errorMessage);
    }
  };

  const generateAndPrintShippingLabel = async function (orderItem) {
    showProgressModal("Generating and printing shipping label...");
    try {
      const callResult = await makePostApiCall(`generateShippingLabel`, {
        shipping_id: shippingId,
        order_item_id: orderItem.id,
        product_id: orderItem.product_id,
      });

      if (callResult.success) {
        const body = callResult.data;
        const labelPayload =
          body &&
          typeof body === "object" &&
          (body.barcode !== undefined || body.customer_name !== undefined)
            ? body
            : (body?.data ?? body);
        hideProgressModal();
        await printShippingLabelToPrinter(labelPayload);
      } else {
        hideProgressModal();
        if (callResult.errorStatus === 401) {
          logout();
        }
        showErrorModal(callResult.data.message);
        return null;
      }
    } catch (error) {
      hideProgressModal();
      showErrorModal(error?.message ?? "Failed to generate shipping label");
      return null;
    }
  };

  return {
    onFetchOrderItems,
    loading,
    setEmptyModelOpen,
    setSlideOver,
    slideOver,
    emptyModal,
    showErrorModal,
    updateShippingApiCall,
    shipping,
    invalidOrderItemIds,
    quickBooksInvoice,
    invoiceQueueEnabled,
    onFetchPackagedItemByBarcode,
    addPackagedItemToOrderItem,
    getShippingDetails,
    removeShippedCartonApiCall,
    employeeOptions,
    getUsersForDropdown,
    deleteShipping,
    printShipping,
    isPrintingShipping,
    quickAddPackagedItemToOrderItem,
    fetchPackagedItemsForQuickScan,
    createInvoiceInQb,
    getOrderItemShipments,
    createShippingRecordByID,
    setProductTemperature,
    generateAndPrintShippingLabel,
    onCreatePriceSplit,
    onUpdatePriceSplit,
    onDeletePriceSplit,
  };
}
